import {
  Alert,
  Button,
  EmptyState,
  FullscreenLayout,
  Icon,
  MarketTileGrid,
  SegmentedControl,
  Skeleton,
  Text,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isQueued } from '../../offline/offline';
import { Link, useParams } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useSession } from '../../identity/session';
import { useMarketDay, useMarketSale, usePrices, type PaymentMethod, type Sale } from '../api';
import { QuickInvoice, type QuickInvoiceSale } from '../components/QuickInvoice';
import { ReferrerField } from '../components/Referrer';
import { formatEuro } from '../format';
import styles from './MarketModePage.module.css';

const METHODS: PaymentMethod[] = ['cash', 'transfer', 'paypal', 'other'];

/**
 * Market mode (MKT-001): big tiles per article, tapping adds one, the total
 * is shown live. Each booking is an anonymous sale, delivered and paid.
 */
export function MarketModePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { marketDayId = '' } = useParams();
  const day = useMarketDay(marketDayId);
  const prices = usePrices();
  const sell = useMarketSale();
  const [cart, setCart] = useState<Record<string, number>>({});
  const [method, setMethod] = useState<PaymentMethod>('cash');
  // "Vermittelt von" (#26): the signed-in person unless chosen otherwise.
  const { me, can } = useSession();
  // The last booking stays at hand for "Rechnung" (MKT-017).
  const [last, setLast] = useState<Sale | null>(null);
  const [lastInvoice, setLastInvoice] = useState<string | null>(null);
  const [invoicing, setInvoicing] = useState<QuickInvoiceSale | null>(null);
  const [referrer, setReferrer] = useState(me.id);
  const back = `/verkauf/markttage/${marketDayId}`;

  if (day.error || prices.error) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={errorMessage(day.error ?? prices.error)}
        actions={<Link to="/verkauf/markttage">{t('sales.market.back')}</Link>}
      />
    );
  }
  if (!day.data || !prices.data) {
    return (
      <div role="status" aria-label={t('common.loading')} className={styles.loading}>
        <Skeleton height="4rem" shape="rect" />
        <Skeleton height="20rem" shape="rect" />
      </div>
    );
  }

  const all = prices.data.filter((a) => !a.archived && a.current_cents != null);
  // With an assortment (MKT-008) the tiles show only its articles, in its
  // order, with the market price; the rest is under "Weitere Artikel".
  const assortment = day.data.assortment;
  const planned = assortment.length > 0;
  const marketPrice = (id: string) => assortment.find((i) => i.article.id === id)?.price_cents;
  const articles = planned
    ? assortment.flatMap((i) => {
        const a = all.find((x) => x.article.id === i.article.id);
        return a ? [a] : [];
      })
    : all;
  const others = planned ? all.filter((a) => !articles.includes(a)) : [];
  const unitPrice = (a: (typeof all)[number]) => marketPrice(a.article.id) ?? a.current_cents ?? 0;
  const price = (id: string) => {
    const a = all.find((x) => x.article.id === id);
    return a ? unitPrice(a) : 0;
  };
  const items = Object.entries(cart).filter(([, n]) => n > 0);
  const total = items.reduce((s, [id, n]) => s + n * price(id), 0);
  const count = items.reduce((s, [, n]) => s + n, 0);
  const closed = day.data.status === 'closed';
  const tile = (a: (typeof all)[number]) => {
    const inCart = cart[a.article.id] ?? 0;
    const item = assortment.find((i) => i.article.id === a.article.id);
    // "Mitgenommen" reserves nothing: the tile shows what is left of it and
    // warns when more would be sold than taken, the sale stays possible.
    const taken = item?.quantity != null ? item.quantity - item.sold - inCart : null;
    return {
      key: a.article.id,
      title: a.article.name,
      subtitle: formatEuro(unitPrice(a)),
      count: inCart,
      hint:
        taken == null
          ? t('sales.marketMode.stock', { count: Math.max(0, a.jars - inCart) })
          : taken >= 0
            ? t('sales.assortment.left', { count: taken })
            : t('sales.assortment.over', { count: -taken }),
      disabled: closed,
    };
  };
  const labels = {
    add: (title: string) =>
      t('sales.marketMode.add', {
        title,
        count: cart[all.find((a) => a.article.name === title)?.article.id ?? ''] ?? 0,
      }),
    remove: (title: string) => t('sales.marketMode.remove', { title }),
  };
  const add = (id: string, delta: number) =>
    setCart((c) => ({ ...c, [id]: Math.max(0, (c[id] ?? 0) + delta) }));
  const book = () =>
    sell.mutate(
      {
        day: marketDayId,
        body: {
          customer_id: null,
          method,
          referred_by_id: referrer,
          lines: items.map(([id, n]) => ({
            article_id: id,
            quantity: n,
            unit_price_cents: null,
            discount_cents: 0,
            batch_id: null,
          })),
        },
      },
      {
        onSuccess: (sale) => {
          if (isQueued(sale)) toast.show(t('offline.captured'), 'info');
          else toast.show(t('sales.marketMode.booked', { total: formatEuro(total) }), 'success');
          // Captured offline: there is no sale to invoice yet.
          setLast(isQueued(sale) ? null : sale);
          setLastInvoice(null);
          setCart({});
        },
      },
    );

  return (
    <FullscreenLayout
      title={day.data.name}
      subtitle={t('sales.marketMode.subtitle', {
        date: formatDate(day.data.held_on),
        count: day.data.summary.sales,
        total: formatEuro(day.data.summary.total_cents),
      })}
      leading={
        <Link to={back} className={styles.close} aria-label={t('sales.marketMode.leave')}>
          <Icon name="close" size="md" />
        </Link>
      }
      footer={
        <div className={styles.footer}>
          <div className={styles.sum} aria-live="polite">
            <Text as="span" size="sm" tone="muted">
              {t('sales.marketMode.items', { count })}
            </Text>
            <span className={styles.total}>{formatEuro(total)}</span>
          </div>
          <div className={styles.footerActions}>
            {count > 0 && (
              <Button variant="ghost" onClick={() => setCart({})}>
                {t('sales.marketMode.clear')}
              </Button>
            )}
            <Button
              size="lg"
              disabled={count === 0 || closed}
              loading={sell.isPending}
              onClick={book}
            >
              {t('sales.marketMode.book')}
            </Button>
          </div>
        </div>
      }
    >
      <div className={styles.content}>
        {closed && <Alert tone="info">{t('sales.market.closedHint')}</Alert>}
        {sell.error && <Alert tone="danger">{errorMessage(sell.error)}</Alert>}
        {last && can('invoices.write') && (
          <div className={styles.last} role="status">
            <Text as="span" size="sm">
              {t('sales.marketMode.last', { total: formatEuro(last.total_cents) })}
            </Text>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setInvoicing(lastInvoice ? { ...last, invoice: { id: lastInvoice } } : last)
              }
            >
              {t('sales.quick.create')}
            </Button>
          </div>
        )}
        {articles.length === 0 ? (
          <EmptyState
            title={t('sales.marketMode.noArticlesTitle')}
            description={t('sales.market.noPrices')}
            actions={<Link to="/verkauf/preise">{t('sales.tabs.prices')}</Link>}
          />
        ) : (
          <>
            <MarketTileGrid
              label={t('sales.marketMode.articles')}
              tiles={articles.map((a) => tile(a))}
              onAdd={(id) => add(id, 1)}
              onRemove={(id) => add(id, -1)}
              labels={labels}
            />
            {others.length > 0 && (
              <details className={styles.others}>
                <summary>{t('sales.assortment.more')}</summary>
                <MarketTileGrid
                  label={t('sales.assortment.more')}
                  tiles={others.map((a) => tile(a))}
                  onAdd={(id) => add(id, 1)}
                  onRemove={(id) => add(id, -1)}
                  labels={labels}
                />
              </details>
            )}
          </>
        )}
        <SegmentedControl
          label={t('sales.form.method')}
          value={method}
          onChange={setMethod}
          fullWidth
          options={METHODS.map((m) => ({ value: m, label: t(`sales.method.${m}`) }))}
        />
        <ReferrerField value={referrer} onChange={setReferrer} />
      </div>
      {invoicing && (
        <QuickInvoice
          sale={invoicing}
          onClose={() => setInvoicing(null)}
          // A second tap on "Rechnung" opens the invoice instead of a new one.
          onCreated={(id) => setLastInvoice(id)}
        />
      )}
    </FullscreenLayout>
  );
}
